import axios from 'axios';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  BULK_ASSIGN_COMPLIANCE_BY_SCOPE_URL,
  CREATE_COMPLIANCE_RULE_URL,
  GET_ALL_FORM_STATUSES_URL,
  GET_ASSIGNED_QUESTIONS_FOR_RULE_URL,
  GET_COMPLIANCE_RULES_URL,
  GET_ENTITY_TYPE_FORMS_COMPLIANCE_URL,
  GET_FORMS_URL,
  get_request_config,
} from '@/utils/system_constants/constant.js';

export default {
  name: 'ComplianceManagement',
  mixins: [alert_mixin],
  data() {
    return {
      loading: false,
      saving: false,
      assigning: false,
      form_statuses: [],
      rules: [],
      entity_type_forms: [],
      assigned_tree: [],
      selected_rule_id: '',
      selected_form_id: '',
      rule_form: {
        name: '',
        description: '',
        initial_grace_period_days: 7,
        frequency_days: '',
        recurring_grace_period_days: '',
        logical_operator: 'AND',
        trigger_status_id: '',
      },
      operators: ['AND', 'OR', 'NOT', 'XOR', 'NAND'],
    };
  },
  computed: {
    filtered_rules() {
      return this.rules;
    },
    selected_rule() {
      return this.rules.find((rule) => String(rule.id) === String(this.selected_rule_id)) || null;
    },
    metrics() {
      return [
        {
          label: 'Compliance Rules',
          value: this.filtered_rules.length,
          icon: 'bi-shield-check',
        },
        {
          label: 'Assigned Forms',
          value: this.entity_type_forms.length,
          icon: 'bi-ui-checks-grid',
        },
        {
          label: 'Rule Questions',
          value: this.count_assigned_questions(this.assigned_tree),
          icon: 'bi-question-circle',
        },
      ];
    },
  },
  watch: {
    selected_rule_id() {
      this.fetch_assigned_questions();
    },
  },
  mounted() {
    this.bootstrap_page();
  },
  methods: {
    parse_response_data(payload) {
      if (typeof payload !== 'string') return payload || {};
      try {
        return JSON.parse(payload);
      } catch {
        return {};
      }
    },
    extract_items(response_data, key) {
      const parsed = this.parse_response_data(response_data);
      const items = parsed[key] || parsed.data || parsed.results || [];
      return Array.isArray(items) ? items : [];
    },
    days_to_duration(days) {
      const parsedDays = Number(days);
      if (!Number.isFinite(parsedDays) || parsedDays <= 0) return null;
      return `${Math.floor(parsedDays)} 00:00:00`;
    },
    count_assigned_questions(forms) {
      return forms.reduce((formTotal, form) => {
        const mainSections = form.main_sections || [];
        return formTotal + mainSections.reduce((mainTotal, mainSection) => {
          const subSections = mainSection.sub_sections || [];
          return mainTotal + subSections.reduce(
            (subTotal, subSection) => subTotal + (subSection.questions || []).length,
            0
          );
        }, 0);
      }, 0);
    },
    async bootstrap_page() {
      this.loading = true;
      try {
        await Promise.all([
          this.fetch_form_statuses(),
          this.fetch_compliance_rules(),
          this.fetch_entity_type_forms(),
        ]);
      } finally {
        this.loading = false;
      }
    },
    async fetch_form_statuses() {
      try {
        const response = await axios.get(GET_ALL_FORM_STATUSES_URL, get_request_config());
        this.form_statuses = this.extract_items(response.data, 'data');
      } catch {
        this.form_statuses = [];
      }
    },
    async fetch_compliance_rules() {
      const response = await axios.get(GET_COMPLIANCE_RULES_URL, get_request_config());
      this.rules = this.extract_items(response.data, 'rules');
      if (!this.selected_rule_id && this.rules.length) {
        this.selected_rule_id = String(this.rules[0].id);
      }
    },
    async fetch_entity_type_forms() {
      try {
        const response = await axios.get(
          GET_FORMS_URL,
          get_request_config()
        );
        const forms = this.extract_items(response.data, 'data');
        this.entity_type_forms = forms.map((form) => ({
          id: form.id || form.form_id,
          form_name: form.form_name || form.name || `Form ${form.id || form.form_id}`,
        })).filter((form) => form.id);
      } catch {
        try {
          const response = await axios.get(
            GET_ENTITY_TYPE_FORMS_COMPLIANCE_URL,
            get_request_config()
          );
          this.entity_type_forms = this.extract_items(response.data, 'data');
        } catch {
        this.entity_type_forms = [];
        }
      }
    },
    async fetch_assigned_questions() {
      if (!this.selected_rule_id) {
        this.assigned_tree = [];
        return;
      }
      try {
        const response = await axios.get(
          GET_ASSIGNED_QUESTIONS_FOR_RULE_URL,
          get_request_config({ params: { compliance_rule_id: this.selected_rule_id } })
        );
        this.assigned_tree = this.extract_items(response.data, 'data');
      } catch {
        this.assigned_tree = [];
      }
    },
    build_rule_payload() {
      const payload = {
        name: this.rule_form.name.trim(),
        description: this.rule_form.description.trim(),
        initial_grace_period: this.days_to_duration(this.rule_form.initial_grace_period_days),
        logical_operator: this.rule_form.logical_operator,
        trigger_status_ids: this.rule_form.trigger_status_id
          ? [Number(this.rule_form.trigger_status_id)]
          : [],
      };

      const frequency = this.days_to_duration(this.rule_form.frequency_days);
      const recurringGrace = this.days_to_duration(this.rule_form.recurring_grace_period_days);
      if (frequency) payload.frequency = frequency;
      if (recurringGrace) payload.recurring_grace_period = recurringGrace;
      return payload;
    },
    reset_rule_form() {
      this.rule_form = {
        name: '',
        description: '',
        initial_grace_period_days: 7,
        frequency_days: '',
        recurring_grace_period_days: '',
        logical_operator: 'AND',
        trigger_status_id: '',
      };
    },
    async create_compliance_rule() {
      if (!this.rule_form.name.trim()) {
        this.show_error('Rule name is required.');
        return;
      }

      this.saving = true;
      const loader = await this.show_loader('Creating compliance rule...');
      try {
        const response = await axios.post(
          CREATE_COMPLIANCE_RULE_URL,
          this.build_rule_payload(),
          get_request_config()
        );
        const responseData = this.parse_response_data(response.data);
        await this.fetch_compliance_rules();
        this.selected_rule_id = responseData.rule_id ? String(responseData.rule_id) : this.selected_rule_id;
        this.reset_rule_form();
        this.show_success(responseData.message || 'Compliance rule created successfully.');
      } catch (error) {
        const responseData = this.parse_response_data(error?.response?.data || {});
        this.show_error(responseData.message || 'Failed to create compliance rule.');
      } finally {
        await this.close_loader(loader);
        this.saving = false;
      }
    },
    async assign_selected_form_to_rule() {
      if (!this.selected_rule_id || !this.selected_form_id) {
        this.show_error('Select a rule and a form to assign.');
        return;
      }

      this.assigning = true;
      const loader = await this.show_loader('Assigning form questions...');
      try {
        const response = await axios.post(
          BULK_ASSIGN_COMPLIANCE_BY_SCOPE_URL,
          {
            compliance_rule_id: Number(this.selected_rule_id),
            scope: 'form',
            scope_id: Number(this.selected_form_id),
          },
          get_request_config()
        );
        const responseData = this.parse_response_data(response.data);
        await this.fetch_assigned_questions();
        this.show_success(
          responseData.message ||
          `${responseData.processed_count || 0} question(s) assigned to the rule.`
        );
      } catch (error) {
        const responseData = this.parse_response_data(error?.response?.data || {});
        this.show_error(responseData.message || 'Failed to assign form questions.');
      } finally {
        await this.close_loader(loader);
        this.assigning = false;
      }
    },
  },
};
